import { BadgeVariant } from '@/components/Badge/Badge.vue';
import { InlineCreateAllocationVariantProps } from '@/components/InlineCreateAllocationVariant/InlineCreateAllocationVariant.vue';
import {
    CreateProjectVariantDocument,
    GetProjectVariantsDocument,
    ProjectBaseDataFragment,
    ProjectVariantDataFragment,
    SetCurrentProjectVariantDocument,
} from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { useMyUser, waitForUser } from '@/user/composables/useMyUser';
import { VariantCardVariant, VariantStatus } from '@/variant/types';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { computed, nextTick, Ref, ref, toValue } from 'vue';
import { ProjectVariantCardExpose } from '../components/ProjectVariantCard.vue';
import { getProjectVariantLinkTitle, getProjectVariantRoute, projectVariantStatusToBadgeVariant } from '../project';

export function useProjectVariants(
    project: Ref<ProjectBaseDataFragment | undefined | null>,
    variantCardCompositionRef: Ref<ProjectVariantCardExpose[]>,
) {
    const variantsRef: Ref<VariantCardVariant[]> = ref([]);
    const inlineCreateStatus: Ref<InlineCreateAllocationVariantProps['status']> = ref('Default');

    const { currentUser } = useMyUser();

    const setCurrentVariantMutation = useMutation(SetCurrentProjectVariantDocument, { fetchPolicy: 'no-cache' });
    const createNewVariantMutation = useMutation(CreateProjectVariantDocument);

    const { loading, error, onResult } = useQuery(
        GetProjectVariantsDocument,
        () => ({ id: project.value!.id }),
        () => ({ enabled: !!project.value }),
    );

    function mapVariantForCard(variant: ProjectVariantDataFragment): VariantCardVariant {
        return {
            ...variant,
            status: variant.status as VariantStatus,
            statusBadgeLabel: $t('event-variant-status', { status: variant.status }),
            statusBadgeVariant: projectVariantStatusToBadgeVariant(variant.status) as BadgeVariant,
            linkTarget: project.value ? getProjectVariantRoute(project.value.id, variant.index) : '',
            linkTitle: project.value ? getProjectVariantLinkTitle(project.value, variant) : '',
            referenceAllocationAreaId: variant.referenceAllocationAreaId ?? undefined,
            referenceAllocationRoomId: variant.referenceAllocationRoomId ?? undefined,
        };
    }

    onResult((result) => {
        if (result.error) {
            throw new Error('Error on receiving variants and comments');
        }
        const data = result.data;
        if (data) {
            const variants = data.variants?.nodes;
            if (variants) {
                variantsRef.value = variants.map((variant) => mapVariantForCard(variant));
            }
        }
    });

    const currentVariant = computed(() => {
        if (variantsRef.value) {
            const variant = toValue(variantsRef).find((v) => v.isCurrent);
            if (!variant) throw new Error('There must be at least one current variant!');
            return variant;
        }
    });

    function setVariantsRef(value: VariantCardVariant[]) {
        variantsRef.value = value;
    }

    async function setCurrentVariant(variantId: string) {
        const variantsState = variantsRef.value;
        const newVariant = variantsState.find((v) => v.id === variantId);
        if (!newVariant) throw new Error('New current variant does not exist');

        if (currentVariant.value) {
            const result = await setCurrentVariantMutation.mutate({
                oldCurrentId: currentVariant.value.id,
                newCurrentId: newVariant.id,
            });

            if (result?.errors) throw new Error('Could not set variant to current variant');

            const oldVariantResult = result?.data?.updateOld?.variant;
            const newVariantResult = result?.data?.updateNew?.variant;

            if (oldVariantResult && newVariantResult) {
                const updatedVariants = variantsState.map((variant) => {
                    if (variant.id === oldVariantResult.id) {
                        return { ...variant, isCurrent: oldVariantResult.isCurrent };
                    }
                    if (variant.id === newVariantResult.id) {
                        return { ...variant, isCurrent: newVariantResult.isCurrent };
                    }
                    return variant;
                });
                setVariantsRef(updatedVariants);

                await nextTick();

                // call child functions of the VariantCards that are changed
                const exposeOldVariant = variantCardCompositionRef.value.find((c) => c.id === oldVariantResult.id);
                await exposeOldVariant?.createNewMainVariantEntry(
                    oldVariantResult.id,
                    oldVariantResult.index,
                    newVariantResult.id,
                    newVariantResult.index,
                );
                const exposeNewVariant = variantCardCompositionRef.value.find((c) => c.id === newVariantResult.id);
                await exposeNewVariant?.createNewMainVariantEntry(
                    oldVariantResult.id,
                    oldVariantResult.index,
                    newVariantResult.id,
                    newVariantResult.index,
                );
            }
        }
    }

    const setCurrentVariantError = computed(() => {
        if (!setCurrentVariantMutation.loading && setCurrentVariantMutation.error) {
            return true;
        }
        return false;
    });

    async function createNewInlineVariant() {
        if (project.value) {
            inlineCreateStatus.value = 'Pending';
            await waitForUser(currentUser);
            let result = undefined;
            try {
                result = await createNewVariantMutation.mutate({
                    id: project.value.id,
                    name: '',
                    createdById: currentUser.value.id,
                    createdByName: currentUser.value.name,
                });
            } catch (error) {
                inlineCreateStatus.value = 'Error';
                return;
            }
            if (result?.errors) {
                inlineCreateStatus.value = 'Error';
                return;
            }
            if (result?.data?.create?.variant) {
                const newVariant = mapVariantForCard(result.data.create?.variant);
                setVariantsRef([newVariant, ...variantsRef.value]);
            }
            inlineCreateStatus.value = 'Default';
        }
    }

    return {
        variantsRef,
        currentVariant,
        loading,
        error,
        setCurrentVariantLoading: setCurrentVariantMutation.loading,
        setCurrentVariantError,
        inlineCreateStatus,
        setVariantsRef,
        setCurrentVariant,
        createNewInlineVariant,
    };
}
